import { Link, useNavigate } from "react-router-dom";
import { Clock, Bed, ForkKnife, Binoculars, Car, WhatsappLogo, Fire } from "@phosphor-icons/react";
import { motion } from "framer-motion";
import { inr } from "@/lib/api";
import { CONTACT } from "@/data/site";

const FALLBACK = "https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";

export default function PackageCard({ pkg, index = 0 }) {
  const navigate = useNavigate();
  const whatsapp = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const msg = `Hi Cluetrip! I want to book the "${pkg.title}" package (${pkg.duration}, ${inr(pkg.price)} per ${pkg.price_note || "person"}). Please share details.`;
    window.open(`https://wa.me/${CONTACT.whatsapp}?text=${encodeURIComponent(msg)}`, "_blank", "noopener");
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 36 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.6, delay: (index % 2) * 0.08, ease: [0.25, 1, 0.5, 1] }}
      data-testid={`package-card-${pkg.id}`}
      className="h-full"
    >
      <div className="group flex h-full flex-col sm:flex-row overflow-hidden rounded-2xl bg-white shadow-[0_8px_30px_rgba(46,42,128,0.10)] transition-shadow duration-300 hover:shadow-[0_18px_44px_rgba(46,42,128,0.20)]">
        {/* IMAGE */}
        <Link to={`/packages/${pkg.id}`} className="relative block sm:w-2/5 shrink-0 overflow-hidden min-h-[190px]">
          <img
            src={pkg.image || FALLBACK}
            alt={pkg.title}
            loading="lazy"
            onError={(e) => { e.currentTarget.src = FALLBACK; }}
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
          />
          <span className="absolute top-4 -left-1 z-10 bg-brand-orange px-4 py-1 text-xs font-bold uppercase tracking-wider text-white shadow-lg [clip-path:polygon(0_0,100%_0,92%_50%,100%_100%,0_100%)] pr-6">
            {pkg.featured ? "Best Offer" : pkg.region}
          </span>
          {pkg.trending && (
            <span className="absolute bottom-3 left-3 z-10 inline-flex items-center gap-1 rounded-full bg-red-600 px-3 py-1 text-[10px] font-extrabold uppercase tracking-wider text-white shadow-lg" data-testid={`trending-badge-${pkg.id}`}>
              <Fire size={12} weight="fill" /> Trending
            </span>
          )}
        </Link>

        {/* CONTENT */}
        <div className="flex flex-1 flex-col p-5">
          <p className="flex items-center gap-1.5 text-xs font-semibold text-brand-teal">
            <Clock size={14} weight="bold" /> {pkg.duration}
          </p>
          <Link to={`/packages/${pkg.id}`}>
            <h3 className="mt-1.5 font-heading text-xl font-semibold text-brand-navy leading-snug transition-colors group-hover:text-brand-teal line-clamp-2">
              {pkg.title}
            </h3>
          </Link>
          <p className="mt-1 text-sm text-[#6b6a8a] font-body line-clamp-1">{pkg.destination}</p>

          <p className="mt-3 flex items-center gap-3 text-xs text-[#8B89A4]">
            Inclusion :
            <span className="flex gap-2.5 text-brand-teal">
              <Bed size={18} weight="duotone" /><ForkKnife size={18} weight="duotone" /><Binoculars size={18} weight="duotone" /><Car size={18} weight="duotone" />
            </span>
          </p>

          <div className="mt-auto pt-4 flex flex-wrap items-end justify-between gap-3">
            <div>
              <p className="font-accent text-2xl font-extrabold text-brand-orange leading-none">{inr(pkg.price)}<span className="text-base">/-</span></p>
              <p className="text-[11px] uppercase tracking-wider text-[#8B89A4] mt-1">Per {pkg.price_note || "Person"}</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => navigate(`/packages/${pkg.id}`)}
                data-testid={`card-view-${pkg.id}`}
                className="rounded-full border-2 border-brand-teal px-4 py-2 text-xs font-bold text-brand-teal transition-colors hover:bg-brand-teal hover:text-white"
              >
                View Details
              </button>
              <button
                onClick={whatsapp}
                data-testid={`card-whatsapp-${pkg.id}`}
                className="inline-flex items-center gap-1.5 rounded-full bg-brand-orange px-4 py-2 text-xs font-bold text-white transition-transform hover:-translate-y-0.5"
              >
                <WhatsappLogo size={15} weight="fill" /> Book Now!
              </button>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
